import { Component, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NavParams, ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { DocumentService } from '../../../services/document.service';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { LoadingService } from '../../../services/loading.service';

@Component({
  selector: 'video-player',
  templateUrl: 'video-player.html',
  styleUrls: ['video-player.scss']
})
export class VideoPlayerComponent implements OnDestroy{
  public endpoint;
  public type;
  public orientation;
  public videoError = false;
  public isLoading = false;
  public localVideoPath: string = null;
  
  @ViewChild('videoElement', { static: false }) videoElement: ElementRef;

  constructor(
    public router: Router, 
    public modalCtrl: ModalController, 
    private navParams: NavParams,
    private documentService: DocumentService,
    private platform: Platform,
    private loadingService: LoadingService
  ) {
    const idVideo = this.navParams.get('idVideo');
    this.orientation = this.navParams.get('orientation');
    this.type = this.navParams.get('type');
    this.endpoint = this.getEndpoint(this.type, idVideo);
    
    // Si es un video nativo/streaming de S3, descargarlo primero para evitar problemas de CORS
    if ((this.type === 'native' || this.type === 'streaming')) {
      const isS3Url = idVideo && (idVideo.includes('s3.amazonaws.com') || idVideo.includes('amazonaws.com'));
      // Descargar si es Android o si es una URL de S3 (para evitar CORS)
      if (this.platform.is('android') || isS3Url) {
        this.downloadAndPlayVideo(idVideo);
      }
    }
  }

  private getEndpoint(type: 'youtube' | 'vimeo' | 'native' | 'streaming', idVideo) {
    if (type === 'vimeo') return `https://player.vimeo.com/video/${idVideo}?autoplay=1`;
    if (type === 'youtube') return `https://empatica-ytvisor.web.app/${idVideo}`;
    
    // Para videos nativos, asegurarse de que la URL sea válida
    if (type === 'native' || type === 'streaming') {
      // Verificar si la URL es válida
      try {
        new URL(idVideo);
        
        // Para URLs de S3, asegurarse de que tengan los headers correctos
        if (idVideo.includes('s3.amazonaws.com')) {
          // En Android, las URLs de S3 pueden tener problemas de CORS
          // Vamos a intentar usar la URL tal como está
        }
      } catch (e) {
        // URL inválida
      }
    }
    
    return idVideo;
  }

  public async closeAlertCancel() {
    this.endpoint = '';
    // Asegurar que se oculte el loading antes de cerrar el modal
    await this.loadingService.hide();
    this.modalCtrl.dismiss('canceled');
  }

  ngAfterViewInit() {
    // Solo configurar los event listeners si no estamos descargando el video
    if (!this.isLoading && this.videoElement && this.videoElement.nativeElement) {
      this.setupVideoEventListeners();
    }
  }

  private setupVideoEventListeners() {
    if (this.videoElement && this.videoElement.nativeElement) {
      const video = this.videoElement.nativeElement;
      
      video.addEventListener('loadstart', () => {
        console.log('Video loadstart event');
      });
      
      video.addEventListener('loadedmetadata', () => {
        console.log('Video loadedmetadata event');
      });
      
      video.addEventListener('canplay', () => {
        console.log('Video canplay event');
      });
      
      video.addEventListener('error', async (e) => {
        console.error('Video error event:', e);
        console.error('Video error details:', video.error);
        
        // Si hay un error y el video es de S3, intentar descargarlo localmente
        const idVideo = this.navParams.get('idVideo');
        const isS3Url = idVideo && (idVideo.includes('s3.amazonaws.com') || idVideo.includes('amazonaws.com'));
        
        if (isS3Url && !this.localVideoPath && !this.isLoading) {
          console.log('Error de CORS detectado, descargando video localmente...');
          await this.downloadAndPlayVideo(idVideo);
        } else {
          this.videoError = true;
        }
      });
      
      video.addEventListener('load', () => {
        console.log('Video load event');
      });
      
      // Timeout para detectar si el video no se carga después de 10 segundos
      setTimeout(() => {
        if (video.readyState === 0) {
          console.error('Video no se pudo cargar después de 10 segundos');
          this.videoError = true;
        }
      }, 10000);
    }
  }

  async downloadAndPlayVideo(videoUrl: string) {
    this.isLoading = true;
    this.videoError = false;
    
    try {
      // En web, usar blob URL para evitar CORS
      if (this.platform.is('capacitor') === false || Capacitor.getPlatform() === 'web') {
        console.log('Descargando video como blob para web...');
        const response = await fetch(videoUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        this.localVideoPath = blobUrl;
        this.endpoint = blobUrl;
        console.log('Video listo para reproducir como blob URL');
      } else {
        // En plataformas nativas, usar Filesystem
        console.log('Descargando video para reproducción local...');
        const fileName = `video_${Date.now()}.mp4`;
        const options = {
          url: videoUrl,
          path: fileName,
          directory: Directory.Data
        };
        
        const downloadedFile = await Filesystem.downloadFile(options);
        console.log('Video descargado:', downloadedFile.path);
        
        this.localVideoPath = downloadedFile.path;
        this.endpoint = Capacitor.convertFileSrc(downloadedFile.path);
        console.log('Video listo para reproducir localmente:', this.localVideoPath);
      }
      
      // Configurar los event listeners después de que el video esté listo
      setTimeout(() => {
        this.setupVideoEventListeners();
      }, 100);
      
    } catch (error) {
      console.error('Error descargando video:', error);
      this.videoError = true;
    } finally {
      this.isLoading = false;
    }
  }

  async downloadVideo() {
    const idVideo = this.navParams.get('idVideo');
    const videoDoc = {
      url: idVideo,
      uri: idVideo,
      name: 'Video',
      extension: 'mp4'
    };
    
    try {
      await this.documentService.downloadDoc(videoDoc, true);
    } catch (error) {
      console.error('Error downloading video:', error);
    }
  }

  ngOnDestroy(): void {
    this.endpoint = '';
    // Liberar blob URL si existe (para web)
    if (this.localVideoPath && this.localVideoPath.startsWith('blob:')) {
      URL.revokeObjectURL(this.localVideoPath);
    }
    // Asegurar que se oculte el loading al destruir el componente
    this.loadingService.hide();
  }
}
